import { lazy, Suspense } from 'react'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

// Lazy load PDFViewer to avoid pdfjs-dist ESM issues with Rspack
const PDFViewer = lazy(() => import('@/components/PDFViewer'))
import { useFilePreviewWithPresigned } from '@/hooks/useFilePreviewWithPresigned'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { clearRegulationsCache } from '@/hooks/useRegulationsCheck'
import { acceptRegulations } from '@/modules/Regulations/services'
import { RegulationsConfig } from '@/modules/Regulations/types'
import useAuthStore from '@/stores/useAuthStore'
import { useCallback, useEffect, useState } from 'react'
import React from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { Button, Checkbox, Typogaphy } from 'vnetwork-ui'

interface RegulationsModalProps {
    regulation: RegulationsConfig
}

const RegulationsModal: React.FC<RegulationsModalProps> = ({ regulation }) => {
    const { t } = useTranslation()
    const { setEmployee, employee } = useAuthStore()
    const { getPresignedUrl } = useFilePreviewWithPresigned()

    const pdfAttachment = regulation.attachments?.find(
        (att) => att.attachment_file_type === 'application/pdf'
    )
    const pdfPath = pdfAttachment?.attachment_file_url

    const [presignedUrl, setPresignedUrl] = useState<string | null>(null)
    const [isLoadingUrl, setIsLoadingUrl] = useState(false)
    const [hasViewedAllPages, setHasViewedAllPages] = useState(false)
    const [hasAgreed, setHasAgreed] = useState(false)
    // Mobile: summary HIỆN mặc định (như bình thường), bấm ▲ để thu gọn lấy chỗ cho PDF
    // (desktop luôn hiện đầy đủ, không có nút)
    const [summaryExpanded, setSummaryExpanded] = useState(true)

    // Fetch presigned URL when pdfPath is available
    useEffect(() => {
        if (pdfPath && !presignedUrl && !isLoadingUrl) {
            setIsLoadingUrl(true)
            getPresignedUrl(pdfPath)
                .then((url) => {
                    setPresignedUrl(url ?? null)
                })
                .finally(() => {
                    setIsLoadingUrl(false)
                })
        }
    }, [pdfPath])

    const handleAllPagesViewed = useCallback(() => {
        setHasViewedAllPages(true)
    }, [])

    const { mutate: submitAcceptance, isPending } = useMutationWithHandler(
        {
            mutationFn: () =>
                acceptRegulations({
                    regulation_version: regulation.regulation_version,
                }),
            onSuccess: () => {
                // Clear old cache so new status will be fetched and cached
                clearRegulationsCache()

                if (employee) {
                    setEmployee({
                        ...employee,
                        regulations_accepted_version:
                            regulation.regulation_version,
                    })
                }
            },
            onError: () => {
                toast.error(t('regulations:regulations-accepted-error'))
            },
        },
        {
            reactQueryKeys: [QUERY_KEYS.GENERIC, 'regulations.stats'],
        }
    )

    const canSubmit = hasViewedAllPages && hasAgreed && !isPending

    // No PDF attachment found
    if (!pdfPath) {
        return (
            <div className="fixed inset-0 z-[99999] bg-white flex flex-col items-center justify-center">
                <p className="text-red-500">
                    {t('regulations:regulation-file-not-found')}
                </p>
            </div>
        )
    }

    return (
        <div className="fixed inset-0 z-[99999] bg-white flex flex-col">
            {/* Header */}
            <nav className="flex-shrink-0 bg-white shadow-sm border-b">
                <div className="px-4 py-3 flex items-center gap-4">
                    <img
                        src="/images/icon.png"
                        alt="VNETWORK"
                        className="h-10 w-auto"
                    />
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <Typogaphy className="text-lg font-semibold text-gray-800 truncate md:overflow-visible md:whitespace-normal">
                                {regulation?.regulation_title ||
                                    t('regulations:company-regulations-title')}
                            </Typogaphy>
                            {regulation.regulation_summary && (
                                <button
                                    type="button"
                                    onClick={() =>
                                        setSummaryExpanded((v) => !v)
                                    }
                                    aria-label={t(
                                        'regulations:company-regulations-title'
                                    )}
                                    className="md:hidden flex-shrink-0 text-gray-500 hover:text-gray-700"
                                >
                                    {summaryExpanded ? (
                                        <ChevronUp className="w-5 h-5" />
                                    ) : (
                                        <ChevronDown className="w-5 h-5" />
                                    )}
                                </button>
                            )}
                        </div>
                        {regulation.regulation_summary && (
                            <p
                                className={`text-sm text-gray-500 ${summaryExpanded ? 'block' : 'hidden'} md:block`}
                            >
                                {regulation?.regulation_summary}
                            </p>
                        )}
                    </div>
                </div>
            </nav>

            {/* PDF Content */}
            <div className="flex-1 overflow-hidden">
                {isLoadingUrl || (!presignedUrl && pdfPath) ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
                    </div>
                ) : presignedUrl ? (
                    <Suspense
                        fallback={
                            <div className="flex items-center justify-center h-full">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
                            </div>
                        }
                    >
                        <PDFViewer
                            url={presignedUrl}
                            onAllPagesViewed={handleAllPagesViewed}
                            disableDownload
                        />
                    </Suspense>
                ) : (
                    <div className="flex items-center justify-center h-full">
                        <p className="text-red-500">
                            {t('error-loading-pdf-file')}
                        </p>
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 border-t bg-white px-4 py-2 shadow-[0_-2px_4px_rgba(0,0,0,0.05)] max-w-3xl mx-auto">
                <div className="flex items-center justify-between gap-4 w-full">
                    <div className="flex items-center gap-2">
                        <Checkbox
                            checked={hasAgreed}
                            onChange={(e) => setHasAgreed(e.target.checked)}
                            disabled={!hasViewedAllPages}
                        />
                        <label
                            className={`text-sm cursor-pointer select-none ${!hasViewedAllPages ? 'text-gray-400' : 'text-gray-700'}`}
                            onClick={() =>
                                hasViewedAllPages && setHasAgreed(!hasAgreed)
                            }
                        >
                            {t('regulations:regulations-agreement-text')}
                            {!hasViewedAllPages && (
                                <span className="text-xs text-amber-600 ml-1">
                                    (
                                    {t(
                                        'regulations:regulations-scroll-all-pages-hint'
                                    )}
                                    )
                                </span>
                            )}
                        </label>
                    </div>
                    <Button
                        onClick={() => submitAcceptance()}
                        disabled={!canSubmit}
                        loading={isPending}
                        variant="contained"
                        size="small"
                    >
                        {t('regulations:accept-regulations')}
                    </Button>
                </div>
            </div>
        </div>
    )
}

export default RegulationsModal
